import React from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Badge,
  Box,
  TextField,
  Theme,
  Typography,
  useTheme
} from '@mui/material';
import { resolveObjectFromString } from '../../../../util/validation';
import { KPIExtended, getKPIs, kpi_label } from '../../../../lib/readiness_gauge/kpi';
import { KPI, Question, Response } from '@esg/esg-global-types';
import { ReactComponent as CheckIcon } from '../../../../assets/icons/Correct.svg';
import { ReactComponent as MissingResponseIcon } from '../../../../assets/icons/Response.svg';

interface KPISelectProps {
  handleChangeKPIs?: (kpis: Array<KPIExtended | KPI> | KPIExtended | KPI | null) => void;
  options?: Array<KPIExtended | KPI>;
  selected_options?: Array<KPIExtended | KPI> | KPIExtended | KPI | null;
  responses?: Array<Response>;
  questions?: Array<Question>;
  disabled?: boolean;
  input_label?: string;
  group_by?: 'kpi_group.name';
  multi_select?: boolean;
  tag_limit?: number;
  clearable?: boolean;
  searchable?: boolean;
  questionnaire_id?: string;
  loading?: boolean;
  color?: string;
  disable_underline?: boolean;
}

/**
 * KPI selection auto-complete component that handles a variety of built-in scenarios.
 * @param {Array<KPIExtended | KPI> | KPIExtended | KPI | null} props.handleChangeKPIs Callback function to notify the parent component when the selected options have changed.
 * @param {Array<KPIExtended | KPI>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {Array<KPIExtended | KPI> | KPIExtended | KPI | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {string} props.group_by Group the options by any one of a variety of grouping options. Defaults to ungrouped.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {boolean} props.searchable Allows the user to type to search in the autocomplete. Defaults to true.
 * @param {string} props.questionnaire_id Query KPIs for the provided Questionnaire.
 * @param {boolean} props.loading A flag to set the autocomplete into a loading state for things such as fetching entries from database.
 * @param {string} props.color A custom color used for the input component of the autocomplete. Defaults to standard MUI color.
 * @param {string} props.disable_underline Allows the hiding of the underline on a standard variant text field
 */
const KPISelect = (props: KPISelectProps) => {
  const [optionList, setOptions] = React.useState<Array<KPIExtended | KPI>>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<
    Array<KPIExtended | KPI> | KPIExtended | KPI | null
  >(props.multi_select ? [] : null);
  const theme: Theme = useTheme();

  const getKPIQuestions = (kpi: KPI | KPIExtended): Array<Question> => {
    return props.questions
      ? props.questions.filter((question: Question) => question.kpi.id === kpi.id)
      : [];
  };

  const getKPIResponses = (kpi: KPI | KPIExtended): Array<Response> => {
    return props.responses
      ? props.responses.filter(
          (response: Response) =>
            !!props.questions?.find(
              (question: Question) =>
                question.id === response.question.id && question.kpi.id === kpi.id
            )
        )
      : [];
  };

  const selected_kpi_complete: boolean | undefined =
    !Array.isArray(selectedOptions) && selectedOptions
      ? getKPIQuestions(selectedOptions).length === getKPIResponses(selectedOptions).length
      : undefined;

  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      const options: Array<KPIExtended | KPI> = props.options
        ? // KPIs supplied by the parent component.
          props.options
        : props.questionnaire_id
          ? // KPIs for specific Questionnaire
            await getKPIs(props.questionnaire_id)
          : [];
      setOptions(options);
    })();
  }, [props.questionnaire_id, props.options]);

  // Define selected options
  React.useEffect(() => {
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : props.selected_options
          ? props.selected_options
          : null
    );
  }, [props.options, props.selected_options]);

  return (
    <Autocomplete
      id="kpi-select"
      fullWidth
      disabled={props.disabled}
      loading={props.loading}
      limitTags={props.tag_limit}
      disableClearable={props.clearable === false}
      options={optionList.sort((a: KPIExtended | KPI, b: KPIExtended | KPI) =>
        a.order > b.order ? 1 : -1
      )}
      groupBy={
        props.group_by
          ? (option: KPIExtended | KPI) =>
              props.group_by ? resolveObjectFromString(props.group_by, option) : ''
          : undefined
      }
      value={selectedOptions}
      getOptionLabel={(option: KPIExtended | KPI) => option.name}
      renderOption={(render_props, option: KPI | KPIExtended) => {
        const kpi_questions: Array<Question> = getKPIQuestions(option);
        const kpi_responses: Array<Response> = getKPIResponses(option);
        const kpi_complete: boolean = kpi_responses.length === kpi_questions.length;
        return (
          <Box
            component="li"
            sx={{ display: 'flex', alignItems: 'center' }}
            {...render_props}
            key={option.id}
          >
            <Typography>{option.name}</Typography>
            {kpi_questions && kpi_responses ? (
              <Badge
                sx={{ my: 1, ml: 'auto', mr: 2 }}
                color={kpi_complete ? 'success' : 'warning'}
                badgeContent={`${kpi_responses.length}/${kpi_questions.length}`}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {kpi_complete ? (
                  <span style={{ color: theme.palette.success.main, display: 'contents' }}>
                    <CheckIcon height={30} fill="none" stroke="currentColor" strokeWidth={4} />
                  </span>
                ) : (
                  <span style={{ color: theme.palette.warning.main, display: 'contents' }}>
                    <MissingResponseIcon
                      height={30}
                      fill="currentColor"
                      stroke="currentColor"
                      strokeWidth={2}
                    />
                  </span>
                )}
              </Badge>
            ) : (
              ''
            )}
          </Box>
        );
      }}
      disableCloseOnSelect={props.multi_select}
      isOptionEqualToValue={(option: KPIExtended | KPI, value: KPIExtended | KPI) =>
        option.id === value.id
      }
      multiple={props.multi_select}
      noOptionsText={`No ${kpi_label.many.toLowerCase()}`}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={props.input_label ?? kpi_label.one}
          variant="standard"
          inputProps={{ ...params.inputProps, readOnly: props.searchable === false }}
          InputProps={{
            ...params.InputProps,
            disableUnderline: props.disable_underline,
            endAdornment: props.multi_select !== true &&
              !Array.isArray(selectedOptions) &&
              selectedOptions && (
                <>
                  <Badge
                    sx={{ my: 1, ml: 'auto', mr: 2 }}
                    color={selected_kpi_complete ? 'success' : 'warning'}
                    badgeContent={`${getKPIResponses(selectedOptions).length}/${getKPIQuestions(selectedOptions).length}`}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {selected_kpi_complete ? (
                      <span style={{ color: theme.palette.success.main, display: 'contents' }}>
                        <CheckIcon height={30} fill="none" stroke="currentColor" strokeWidth={4} />
                      </span>
                    ) : (
                      <span style={{ color: theme.palette.warning.main, display: 'contents' }}>
                        <MissingResponseIcon
                          height={30}
                          fill="currentColor"
                          stroke="currentColor"
                          strokeWidth={2}
                        />
                      </span>
                    )}
                  </Badge>
                  {params.InputProps.endAdornment}
                </>
              )
          }}
        />
      )}
      onChange={(
        event: React.SyntheticEvent<Element, Event>,
        value: Array<KPIExtended | KPI> | KPIExtended | KPI | null
      ) => {
        setSelectedOptions(value);
        props.handleChangeKPIs && props.handleChangeKPIs(value);
      }}
    />
  );
};

export default KPISelect;
